import {
  getQuery,
  UUID,
  getSS,
  setSS,
  getLS,
  setLS,
  getTimezoneName,
  delSS,
  getCookie,
} from './utils';
import {
  ENUM_SYSTEM,
  detectSystem,
  ENUM_BROWSER,
  detectBrowser,
  detectApp,
  ruleMatch,
  versionMatch,
} from './libs/detect';
import { useInteractions } from './libs/interactions';

const computeCST = () => {
  if (window.CST_MESC) {
    return window.CST_MESC * 1e3;
  }
  return new Date().getTime();
};

const computeSST = () => {
  const SST_MESC = getCookie('SST_MESC');
  if (SST_MESC) {
    return SST_MESC * 1e3;
  }
  return new Date().getTime();
};

/**
 * App运行时上下文
 */
const Ctx = class {
  constructor($config) {
    // 环境常量
    this.RUNTIME = {
      PROD: 'prod',
      DEV: 'dev',
      TEST: 'test',
      PRE: 'pre',
    };
    // 系统
    this.SYSTEM = ENUM_SYSTEM;
    // 浏览器
    this.BROWSER = ENUM_BROWSER;
    this.CLIENT = {
      PC_Web: 'PC_Web',
      PC_PWA: 'PC_PWA',
      PC_App: 'PC_App',
      Pad_Web: 'Pad_Web',
      Pad_PWA: 'Pad_PWA',
      Pad_App: 'Pad_App',
      Mobile_Web: 'Mobile_Web',
      Mobile_PWA: 'Mobile_PWA',
      Mobile_App: 'Mobile_App',
      Other: 'Other',
    };
    this.VIEW = {
      MO: 'm',
      PAD: 'pad',
      PC: 'pc',
    };

    // 基础环境值常量KEY
    this.CHANNEL_KEY = $config.log.channelKey;
    this.CHANNEL_LEVEL_KEY = $config.log.channelLevelKey;
    this.REFERRAL_CODE_KEY = $config.log.referralKey;
    this.SESSION_ID_KEY = $config.site.sessionIDKey;
    this.DEVICD_ID_KEY = $config.site.deviceIDKey;
    this.PAGE_RECORDS_KEY = $config.site.pageRecordsKey;

    // 加载完成事件承诺
    this.onload = new Promise(resolve => {
      window.addEventListener('load', () => {
        window.setTimeout(() => {
          resolve();
        }, 10);
      }, { once: true });
    });

    // Dom Ready时事件承诺
    this.onready = new Promise(resolve => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
      } else {
        resolve();
      }
    });

    // App Ready时的承诺
    this.$AppReadyResolve = null;
    this.onAppReady = new Promise(resolve => {
      this.$AppReadyResolve = resolve;
    });

    this.isDarkMode = () => window.matchMedia('(prefers-color-scheme: dark)').matches;
    // 获取根元素字体大小（REM适配使用）
    this.getRootFontSize = () => parseFloat(document.documentElement.style.fontSize) || 100;

    // 暴露的可用属性
    this.query = getQuery(); // 初始nvp参数
    this.runtime = $config.runtime; // 运行环境
    this.publicPath = process.env.BASE_URL; // 静态资源路径
    this.timezone = getTimezoneName(); // 时区
    this.system = detectSystem(); // 系统类型
    this.appInfo = detectApp(); // App内嵌信息
    this.browser = detectBrowser(); // 浏览器类型
    this.view = this.$ComputeView(); // 视图类型
    this.client = this.$ComputeClient(); // 客户端类型
    this.version = new Date(window.$hobbit_preset_config?.buildTime || +new Date()).toOffsetDate(-480).format('yyyy.MM.dd.hhmmss'); // 版本号
    this.channel = this.$ComputeChannel(); // 渠道号
    this.channelLevel = this.$ComputeChannelLevel(); // 渠道级别
    this.referralCode = this.$ComputeReferralCode(); // 转介绍码
    const session = this.$ComputeSessionID(); // 会话ID
    this.sessionID = session.sessionID;
    this.isNewSession = session.isNewSession;
    this.interactions = useInteractions(); // 最近用户交互数据
    const device = this.$ComputeDeviceID(); // 设备ID
    this.deviceID = device.deviceID;
    this.isNewDevice = device.isNewDevice;
    this.startPage = null; // 起始页
    this.prevPage = null; // 上一页
    this.currPage = null; // 当前页
    this.pageIndex = 0; // 页次序（每次路由变动+1, SPA启动页是1）
    // 语言和国家码初始化逻辑在main.js中做
    this.lang = null; // 语言码
    this.biz = null; // 业务线码
    this.revertLang = null; // 降级语言

    this.CST = computeCST();
    this.SST = computeSST();
  }

  isDev() {
    return this.runtime === this.RUNTIME.DEV;
  }

  isProd() {
    return this.runtime === this.RUNTIME.PROD;
  }

  isTest() {
    return this.runtime === this.RUNTIME.TEST;
  }

  isPre() {
    return this.runtime === this.RUNTIME.PRE;
  }

  isMO() {
    return this.view === this.VIEW.MO;
  }

  isPAD() {
    return this.view === this.VIEW.PAD;
  }

  isPC() {
    return this.view === this.VIEW.PC;
  }

  isApp(excludePCApp = true) {
    if (excludePCApp) {
      return !!this.appInfo?.name && this.appInfo?.name !== 'VIPKID PC STUDY';
    }
    return !!this.appInfo?.name;
  }

  isPWA() {
    // eslint-disable-next-line no-underscore-dangle
    const pwaFlag = this.query._pwa_;
    if (pwaFlag) {
      setSS('_pwa_', pwaFlag);
      return true;
    }
    return !!getSS('_pwa_');
  }

  /**
   * 上下文匹配
   */
  match(condition, reverseCondition) {
    let conditionPass = true;
    if (condition) {
      if (condition.runtime && !ruleMatch(condition.runtime, this.runtime)) conditionPass = false;
      else if (condition.vt && !ruleMatch(condition.vt, this.view)) conditionPass = false;
      else if (condition.lang && !ruleMatch(condition.lang, this.lang)) conditionPass = false;
      else if (condition.biz && !ruleMatch(condition.biz, this.biz)) conditionPass = false;
      else if (condition.system && !ruleMatch(condition.system, this.system)) conditionPass = false;
      else if (condition.client && !ruleMatch(condition.client, this.client)) conditionPass = false;
      else if (condition.appName && !ruleMatch(condition.appName, this.appInfo.name)) conditionPass = false;
      else if (condition.appProtocol && !ruleMatch(condition.appProtocol, this.appInfo.protocol)) conditionPass = false;
      else if (condition.appPlatform && !ruleMatch(condition.appPlatform, this.appInfo.platform)) conditionPass = false;
      else if (condition.appVersion && !versionMatch(condition.appVersion, this.appInfo.version)) conditionPass = false;
    }

    let reverseConditionPass = true;
    if (reverseCondition) {
      if (reverseCondition.runtime && ruleMatch(reverseCondition.runtime, this.runtime)) reverseConditionPass = false;
      else if (reverseCondition.vt && ruleMatch(reverseCondition.vt, this.view)) reverseConditionPass = false;
      else if (reverseCondition.lang && ruleMatch(reverseCondition.lang, this.lang)) reverseConditionPass = false;
      else if (reverseCondition.biz && ruleMatch(reverseCondition.biz, this.biz)) reverseConditionPass = false;
      else if (reverseCondition.system && ruleMatch(reverseCondition.system, this.system)) reverseConditionPass = false;
      else if (reverseCondition.client && ruleMatch(reverseCondition.client, this.client)) reverseConditionPass = false;
      else if (reverseCondition.appName && ruleMatch(reverseCondition.appName, this.appInfo.name)) reverseConditionPass = false;
      else if (reverseCondition.appProtocol && ruleMatch(reverseCondition.appProtocol, this.appInfo.protocol)) reverseCondition = false;
      else if (reverseCondition.appPlatform && ruleMatch(reverseCondition.appPlatform, this.appInfo.platform)) reverseConditionPass = false;
      else if (reverseCondition.appVersion && versionMatch(reverseCondition.appVersion, this.appInfo.version)) reverseConditionPass = false;
    }
    return conditionPass && reverseConditionPass;
  }

  /**
   * 获取相对准确的客户端时间戳
   */
  getTime() {
    if (this.CST && this.SST) {
      const offset = this.CST - this.SST;
      if (Number.isInteger(offset)) {
        return new Date().getTime() - offset;
      }
    }
    return new Date().getTime();
  }

  /**
   * 计算会话ID
   */
  $ComputeSessionID() {
    let sessionID = getSS(this.SESSION_ID_KEY);
    if (Ctx.checkUUID(sessionID)) {
      return { isNewSession: false, sessionID };
    }
    sessionID = UUID();
    setSS(this.SESSION_ID_KEY, sessionID);
    return { isNewSession: true, sessionID };
  }

  /**
   * 计算设备ID
   */
  $ComputeDeviceID() {
    let deviceID = getLS(this.DEVICD_ID_KEY);
    if (Ctx.checkUUID(deviceID)) {
      return { isNewDevice: false, deviceID };
    }
    deviceID = UUID();
    setLS(this.DEVICD_ID_KEY, deviceID);
    return { isNewDevice: true, deviceID };
  }

  /**
   * 计算App视图类型（前提是在App内，否则返回null）
   */
  $ComputeAppView() {
    if (this.appInfo?.name === 'VIPKID PC STUDY') return this.VIEW.PC;
    if (this.appInfo?.name) {
      if (/pad$/i.test(this.appInfo?.platform)) return this.VIEW.PAD;
      return this.VIEW.MO;
    }
    return null;
  }

  /**
   * 计算视图类型
   */
  $ComputeView() {
    // 参数强制
    // eslint-disable-next-line no-underscore-dangle
    if (this.query._view_) {
      // eslint-disable-next-line no-underscore-dangle
      return this.query._view_;
    }

    // 如果是App内嵌模式，则以App端视图类型为准
    const appView = this.$ComputeAppView();
    if (appView) {
      return appView;
    }

    const width = window.screen.width;
    const height = window.screen.height;
    const minSide = Math.min(width, height);
    const maxSide = Math.max(width, height);
    // 短边小于阈值，则为MO视图
    if (minSide < 500) {
      return this.VIEW.MO;
    }
    // 高度小于阈值，且是横屏状态，则为MO视图
    // Android Webview 屏幕旋转角度获取有延时Bug，因此尽量不要使用旋转角度判断
    // if (height < 500 && !!(window.screen.orientation?.angle ?? window.orientation ?? 0)) {
    //   return this.VIEW.MO;
    // }

    // 长边和短边小于阈值，则为PAD视图
    if (minSide < 1024 && maxSide < 1280) {
      return this.VIEW.PAD;
    }
    return this.VIEW.PC;
  }

  /**
   * 计算客户端类型
   */
  $ComputeClient() {
    const appView = this.$ComputeAppView();
    switch (appView) {
      case this.VIEW.PC: return this.CLIENT.PC_App;
      case this.VIEW.PAD: return this.CLIENT.Pad_App;
      case this.VIEW.MO: return this.CLIENT.Mobile_App;
      default:
        switch (this.view) {
          case this.VIEW.PC:
            return this.isPWA() ? this.CLIENT.PC_PWA : this.CLIENT.PC_Web;
          case this.VIEW.PAD:
            return this.isPWA() ? this.CLIENT.Pad_PWA : this.CLIENT.Pad_Web;
          case this.VIEW.MO:
            return this.isPWA() ? this.CLIENT.Mobile_PWA : this.CLIENT.Mobile_Web;
          default:
            return this.CLIENT.Other;
        }
    }
  }

  /**
   * 计算渠道码
   */
  $ComputeChannel() {
    const channel = this.query[this.CHANNEL_KEY];
    if (Ctx.checkChannel(channel)) {
      setSS(this.CHANNEL_KEY, channel);
      return channel;
    }

    return getSS(this.CHANNEL_KEY);
  }

  /**
   * 手动设置渠道号
   */
  setChannel(channel) {
    if (Ctx.checkChannel(channel)) {
      this.channel = channel;
      setSS(this.CHANNEL_KEY, channel);
    }
  }

  /**
   * 计算渠道级别
   */
  $ComputeChannelLevel() {
    const channellevel = this.query[this.CHANNEL_LEVEL_KEY];
    if (channellevel) {
      setSS(this.CHANNEL_LEVEL_KEY, channellevel);
      return channellevel;
    }
    return getSS(this.CHANNEL_LEVEL_KEY);
  }

  /**
   * 手动设置渠道级别
   */
  setChannelLevel(channelLevel) {
    if (channelLevel) {
      this.channelLevel = channelLevel;
      setSS(this.CHANNEL_KEY, channelLevel);
    }
  }

  /**
   * 计算转介绍码
   */
  $ComputeReferralCode() {
    const referralCode = /[a-z0-9]+/i.exec(this.query[this.REFERRAL_CODE_KEY] ?? '')?.[0];
    if (referralCode) {
      setSS(this.REFERRAL_CODE_KEY, referralCode);
      return referralCode;
    }
    return getSS(this.REFERRAL_CODE_KEY);
  }

  /**
   * 手动设置转介绍码
   */
  setReferralCode(referralCode) {
    if (referralCode) {
      this.referralCode = referralCode;
      setSS(this.REFERRAL_CODE_KEY, referralCode);
    } else {
      this.referralCode = '';
      delSS(this.REFERRAL_CODE_KEY);
    }
  }

  /**
   * px单位值转rem单位值
   */
  px2rem(px) {
    return parseFloat(px / this.getRootFontSize());
  }

  /**
   * rem单位值转px单位值
   */
  rem2px(rem) {
    return parseFloat(rem * this.getRootFontSize());
  }

  /**
   * 校验渠道
   */
  static checkChannel(from) {
    return from && /^[\w-]{2,32}$/i.test(from);
  }

  /**
   * 校验UUID
   */
  static checkUUID(uuid) {
    return uuid && /^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(uuid);
  }

  /**
   * 记录页面名称
   * 如果是嵌套页面，不再记录至SS。避免父子页面同时记录造成统计混乱
   */
  afterEachPage(to, from) {
    // 路由变化，页序+1
    this.pageIndex += 1;
    const time = new Date().getTime();
    const page = { name: to.name, time };
    if (this.PAGE_RECORDS_KEY && this.currPage === null && window.top === window) {
      const records = getSS(this.PAGE_RECORDS_KEY);
      if (records) {
        this.currPage = records.curr;
        this.prevPage = records.prev;
        this.startPage = records.start;
      }
    }

    // 有前一页且24小时内才算做同一个会话周期内连续访问
    if (this.currPage && time - this.currPage.time < 24 * 3600 * 1000) {
      this.prevPage = this.currPage;
      this.currPage = page;
    } else {
      this.startPage = page;
      this.prevPage = null;
      this.currPage = page;
    }

    if (this.PAGE_RECORDS_KEY && window.top === window) {
      setSS(this.PAGE_RECORDS_KEY, {
        start: this.startPage,
        prev: this.prevPage,
        curr: this.currPage,
      });
    }
  }
};

export default Ctx;
